import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Users,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Package,
  CreditCard,
  UserCheck,
  UserX,
  Calendar,
  Crown,
  Save,
  X,
  Key,
  Eye,
  EyeOff,
  Plus,
  Copy,
  Check,
  QrCode,
  MessageCircle,
  UserPlus,
  CheckCircle,
  XCircle,
  History,
  ChevronDown
} from 'lucide-react';
import QRCode from 'qrcode';
import { toast } from 'react-hot-toast';
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import {
  sendPasswordResetEmail,
  updatePassword,
  getAuth
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { User, ClassPassType, UserRole, UserStatus, MembershipType } from '../../types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';
import { BlinkingNotification } from '../../components/ui/BlinkingNotification';
import { ProfilePhoto } from '../../components/ui/ProfilePhoto';
import {
  calculateDaysRemaining,
  formatDate
} from '../../utils/membershipUtils';
import AdminChatModal from '../../components/admin/AdminChatModal';

interface UserFilter {
  role?: UserRole;
  status?: UserStatus;
  membershipType?: MembershipType;
  search?: string;
}

// Interfaz para membresías del negocio
interface BusinessMembership {
  id: string;
  name: string;
  price: number;
  reservationsPerMonth: number;
  timeRestriction: 'all_day' | 'morning' | 'afternoon';
  classRestriction: 'all' | 'specific';
  allowedClassTypeIds?: string[];
  isActive: boolean;
  weeklyLimitEnabled?: boolean;
  weeklyClassCount?: number;
}

// Interfaz para membresía de usuario
interface UserMembershipData {
  id: string;
  membershipId: string;
  membershipName: string;
  userId: string;
  businessId: string;
  startDate: Date;
  renewalDate: Date;
  reservationsUsed: number;
  reservationsRemaining: number;
  reservationsPerMonth: number;
  timeRestriction: 'all_day' | 'morning' | 'afternoon';
  classRestriction: 'all' | 'specific';
  allowedClassTypeIds?: string[];
  isActive: boolean;
  isPaused: boolean;
  weeklyLimitEnabled?: boolean;
  weeklyClassCount?: number;
  autoRenewal?: boolean; // Renovación automática
}

const UserManagementPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const { config: systemConfig } = useSystemConfig();
  const [users, setUsers] = useState<User[]>([]);
  const [passTypes, setPassTypes] = useState<ClassPassType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<UserFilter>({});
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'active' as UserStatus,
    membershipType: 'monthly_fee' as MembershipType,
    paymentStatus: 'pending' as 'paid' | 'pending'
  });

  // Estados para restablecimiento de contraseña
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Estado para copiar enlace de invitación
  const [copiedLink, setCopiedLink] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  // Estados para editar fecha de inicio de membresía
  const [editMembershipDateModal, setEditMembershipDateModal] = useState(false);
  const [selectedUserForDateEdit, setSelectedUserForDateEdit] = useState<User | null>(null);
  const [newMembershipDate, setNewMembershipDate] = useState('');
  const [dateEditLoading, setDateEditLoading] = useState(false);

  // Estados para chat
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedUserForChat, setSelectedUserForChat] = useState<User | null>(null);
  const [usersWithUnreadMessages, setUsersWithUnreadMessages] = useState<Set<string>>(new Set());

  // Estado para panel de detalles del usuario
  const [selectedUserDetails, setSelectedUserDetails] = useState<User | null>(null);

  // Estado para mostrar/ocultar historial de membresías
  const [showHistory, setShowHistory] = useState(false);

  // Estados para membresías del negocio
  const [businessMemberships, setBusinessMemberships] = useState<BusinessMembership[]>([]);
  const [userMemberships, setUserMemberships] = useState<{ [userId: string]: UserMembershipData }>({});

  // Estado para modal de asignar membresía/bono
  const [assignModal, setAssignModal] = useState<{ open: boolean; user: User | null; type: 'membership' | 'pass' }>({
    open: false,
    user: null,
    type: 'membership'
  });

  // Generar enlace limpio dinámicamente usando el slug
  const getCleanInviteLink = () => {
    if (!currentBusiness?.slug) return '';
    return `${window.location.origin}/register/${currentBusiness.slug}`;
  };

  // Generar código QR cuando cambie el negocio
  useEffect(() => {
    const cleanLink = getCleanInviteLink();
    if (cleanLink) {
      QRCode.toDataURL(cleanLink, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((url) => {
          setQrCodeUrl(url);
        })
        .catch((err) => {
          console.error('Error generating QR code:', err);
        });
    }
  }, [currentBusiness]);

  // OPTIMIZACIÓN: Usar listener en tiempo real en lugar de polling cada 30 segundos
  // Esto reduce significativamente las lecturas (de ~2880/día a solo cuando hay cambios)
  useEffect(() => {
    if (!businessId) return;

    const chatsQuery = query(
      collection(db, 'chats'),
      where('businessId', '==', businessId),
      where('senderRole', '==', 'user'),
      where('isRead', '==', false)
    );

    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      const userIds = new Set<string>();
      snapshot.docs.forEach(docSnap => {
        const senderId = docSnap.data().senderId;
        if (senderId) userIds.add(senderId);
      });
      setUsersWithUnreadMessages(userIds);
    }, (error) => {
      console.error('Error en listener de chats no leídos:', error);
    });

    return () => unsubscribe();
  }, [businessId]);

  // Handle deep link to chat
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openChatUserId = searchParams.get('openChat');
    if (openChatUserId && users.length > 0) {
      const userToChat = users.find(u => u.id === openChatUserId);
      if (userToChat) {
        setSelectedUserForChat(userToChat);
        setChatModalOpen(true);
        // Clear param
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('openChat');
        setSearchParams(newParams);
      }
    }
  }, [users, searchParams, setSearchParams]);

  // Cargar usuarios
  const loadUsers = async () => {
    if (!businessId) return;

    try {
      console.log('🔄 Cargando usuarios desde Firestore...');

      const q = query(collection(db, 'users'), where('businessId', '==', businessId));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const user: User = {
          id: doc.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          role: data.role || 'user',
          status: data.status || 'active',
          membershipType: data.membershipType || 'monthly_fee',
          paymentStatus: data.paymentStatus || 'pending',
          membershipStartDate: data.membershipStartDate?.toDate() || null,
          lastPaymentDate: data.lastPaymentDate?.toDate() || null,
          nextDueDate: data.nextDueDate?.toDate() || null,
          membershipHistory: data.membershipHistory || [],
          activePass: data.activePass || null,
          profilePhotoUrl: data.profilePhotoUrl || undefined,
          businessId: data.businessId || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };

        console.log('👤 Usuario cargado:', {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          photoUrl: user.profilePhotoUrl
        });

        return user;
      });

      console.log('📸 Total de usuarios cargados:', usersData.length);
      console.log('📸 Usuarios con fotos:', usersData.filter(u => u.profilePhotoUrl).length);

      setUsers(usersData);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Cargar tipos de bonos
  const loadPassTypes = async () => {
    if (!businessId) return;

    try {
      console.log('🔄 Cargando tipos de bonos desde Firestore...');

      const q = query(collection(db, 'passTypes'), where('businessId', '==', businessId));
      const querySnapshot = await getDocs(q);
      const passes = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as ClassPassType[];

      passes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      console.log(`✅ ${passes.length} tipos de bonos cargados exitosamente:`, passes);
      setPassTypes(passes);
    } catch (error: any) {
      console.error('❌ Error cargando tipos de bonos:', error);
      setPassTypes([]);
    }
  };

  // Cargar membresías del negocio
  const loadBusinessMemberships = async () => {
    if (!businessId) return;

    try {
      const q = query(collection(db, 'memberships'), where('businessId', '==', businessId));
      const querySnapshot = await getDocs(q);
      const memberships = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        price: doc.data().price,
        reservationsPerMonth: doc.data().reservationsPerMonth,
        timeRestriction: doc.data().timeRestriction || 'all_day',
        classRestriction: doc.data().classRestriction || 'all',
        allowedClassTypeIds: doc.data().allowedClassTypeIds || [],
        isActive: doc.data().isActive
      })) as BusinessMembership[];

      setBusinessMemberships(memberships.filter(m => m.isActive));
    } catch (error) {
      console.error('Error cargando membresías:', error);
    }
  };

  // Cargar membresías de usuarios
  // OPTIMIZACIÓN: Usar businessMemberships ya cargadas en lugar de hacer N+1 queries
  const loadUserMemberships = async () => {
    if (!businessId) return;

    try {
      // Primero asegurarse de que businessMemberships esté cargado
      let membershipsLookup = businessMemberships;
      if (membershipsLookup.length === 0) {
        // Si aún no se cargaron, cargarlas aquí
        const membershipsQuery = query(collection(db, 'memberships'), where('businessId', '==', businessId));
        const membershipsSnapshot = await getDocs(membershipsQuery);
        membershipsLookup = membershipsSnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          name: docSnap.data().name,
          price: docSnap.data().price,
          reservationsPerMonth: docSnap.data().reservationsPerMonth,
          timeRestriction: docSnap.data().timeRestriction || 'all_day',
          classRestriction: docSnap.data().classRestriction || 'all',
          allowedClassTypeIds: docSnap.data().allowedClassTypeIds || [],
          isActive: docSnap.data().isActive,
          weeklyLimitEnabled: docSnap.data().weeklyLimitEnabled,
          weeklyClassCount: docSnap.data().weeklyClassCount
        })) as BusinessMembership[];
      }

      // Crear mapa de membresías para lookup rápido
      const membershipsMap: Record<string, BusinessMembership> = {};
      membershipsLookup.forEach(m => { membershipsMap[m.id] = m; });

      const q = query(collection(db, 'userMemberships'), where('businessId', '==', businessId));
      const querySnapshot = await getDocs(q);
      const memberships: { [userId: string]: UserMembershipData } = {};

      // OPTIMIZACIÓN: Usar mapa en memoria en lugar de queries individuales
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        const membershipData = membershipsMap[data.membershipId];

        memberships[data.userId] = {
          id: docSnap.id,
          membershipId: data.membershipId,
          membershipName: membershipData?.name || 'Membresía',
          userId: data.userId,
          businessId: data.businessId,
          startDate: data.startDate?.toDate() || new Date(),
          renewalDate: data.renewalDate?.toDate() || new Date(),
          reservationsUsed: data.reservationsUsed || 0,
          reservationsRemaining: data.reservationsRemaining || 0,
          reservationsPerMonth: membershipData?.reservationsPerMonth || 0,
          timeRestriction: membershipData?.timeRestriction || 'all_day',
          classRestriction: membershipData?.classRestriction || 'all',
          allowedClassTypeIds: membershipData?.allowedClassTypeIds || [],
          isActive: data.isActive,
          isPaused: data.isPaused || false,
          weeklyLimitEnabled: membershipData?.weeklyLimitEnabled,
          weeklyClassCount: membershipData?.weeklyClassCount,
          autoRenewal: data.autoRenewal || false
        };
      }

      setUserMemberships(memberships);
    } catch (error) {
      console.error('Error cargando membresías de usuarios:', error);
    }
  };

  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      await Promise.all([loadUsers(), loadPassTypes(), loadBusinessMemberships(), loadUserMemberships()]);
      setLoading(false);
    };
    loadData();
  }, [businessId]);

  // Filtrar usuarios - mostrar todos los usuarios (incluyendo admins)
  const filteredUsers = users.filter(user => {
    if (filter.status && user.status !== filter.status) return false;
    if (filter.membershipType && user.membershipType !== filter.membershipType) return false;
    if (filter.search) {
      const searchTerm = filter.search.toLowerCase();
      return (
        user.firstName.toLowerCase().includes(searchTerm) ||
        user.lastName.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm)
      );
    }
    return true;
  });

  // Obtener estado de suscripción del usuario (sincronizado con userMemberships, activeMembership y activePass)
  const getSubscriptionStatus = (user: User) => {
    const userMembershipData = userMemberships[user.id];

    // Verificar bono activo - independiente del membershipType
    const hasPass = user.activePass &&
      user.activePass.creditsRemaining > 0 &&
      !user.activePass.isBlocked &&
      (!user.activePass.expirationDate || new Date(user.activePass.expirationDate) > new Date());

    // Verificar membresía activa - independiente del membershipType
    const hasMembershipFromCollection = userMembershipData?.isActive && !userMembershipData?.isPaused;
    const hasMembershipFromUser = user.activeMembership?.isActive && !user.activeMembership?.isPaused;
    const hasMembership = hasMembershipFromCollection || hasMembershipFromUser;

    // Verificar membresía expirada o pausada
    const membershipPaused = userMembershipData?.isPaused || user.activeMembership?.isPaused;

    // Si tiene AMBOS activos, mostrarlo
    if (hasPass && hasMembership) {
      return { label: 'Membresía + Bono', color: 'bg-gradient-to-r from-green-900/50 to-blue-900/50 text-emerald-400', icon: '✓🎟️' };
    }

    // Si tiene membresía pausada y bono activo
    if (hasPass && membershipPaused) {
      return { label: 'Bono (M. Pausada)', color: 'bg-blue-900/50 text-blue-400', icon: '🎟️⏸️' };
    }

    // Si solo tiene bono
    if (hasPass) {
      return { label: 'Bono', color: 'bg-blue-900/50 text-blue-400', icon: '🎟️' };
    }

    // Si solo tiene membresía
    if (hasMembership) {
      return { label: 'Membresía', color: 'bg-green-900/50 text-green-400', icon: '✓' };
    }

    // Si tiene membresía pausada
    if (membershipPaused) {
      return { label: 'Membresía Pausada', color: 'bg-yellow-900/50 text-yellow-400', icon: '⏸️' };
    }

    // Bono caducado (sin créditos o expirado)
    if (user.activePass && (
      user.activePass.creditsRemaining <= 0 ||
      (user.activePass.expirationDate && new Date(user.activePass.expirationDate) < new Date())
    )) {
      return { label: 'Bono Agotado', color: 'bg-red-900/50 text-red-400', icon: '⚠️' };
    }

    // Sin suscripción
    return { label: 'Pendiente', color: 'bg-yellow-900/50 text-yellow-400', icon: '⏳' };
  };

  // Iniciar edición de usuario
  const startEditUser = (user: User) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || '',
      status: user.status,
      membershipType: user.membershipType,
      paymentStatus: user.paymentStatus || 'pending'
    });
  };

  // Guardar cambios del usuario
  const saveUserChanges = async () => {
    if (!editingUser) return;

    try {
      const updatedData = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        status: editForm.status,
        membershipType: editForm.membershipType,
        paymentStatus: editForm.paymentStatus,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'users', editingUser.id), updatedData);
      toast.success('Usuario actualizado exitosamente');
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      toast.error('Error al actualizar el usuario');
    }
  };

  // Cancelar edición
  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      status: 'active',
      membershipType: 'monthly_fee',
      paymentStatus: 'pending'
    });
  };

  // FUNCIÓN PRINCIPAL PARA ASIGNAR BONO - USANDO PROMPT/CONFIRM
  const assignBonusToUser = async (user: User) => {
    console.log('🎯 Iniciando asignación de bono para:', user.firstName);
    console.log('📊 Estado actual - passTypes:', passTypes);
    console.log('📊 Usuario:', user);

    // Verificar si hay tipos de bonos disponibles
    if (passTypes.length === 0) {
      console.log('❌ No hay tipos de bonos disponibles');
      alert('❌ No hay tipos de bonos disponibles.\n\nVe a la sección "Passes" para crear algunos tipos de bonos primero.');
      return;
    }

    console.log('✅ Hay tipos de bonos disponibles, mostrando opciones...');

    // Mostrar opciones disponibles
    const options = passTypes.map((pass, index) =>
      `${index + 1}. ${pass.passName} - ${pass.classCredits} clases (${pass.validityInDays} días)`
    ).join('\n');

    const message = `Asignar bono a ${user.firstName} ${user.lastName}\n\nTipos de bonos disponibles:\n${options}\n\nEscribe el número del bono que quieres asignar:`;

    console.log('📝 Mostrando prompt con mensaje:', message);
    const selection = prompt(message);

    console.log('🎯 Selección del usuario:', selection);

    if (!selection) {
      console.log('❌ Usuario canceló la selección');
      return;
    }

    const selectedIndex = parseInt(selection) - 1;
    console.log('📊 Índice seleccionado:', selectedIndex);

    if (isNaN(selectedIndex) || selectedIndex < 0 || selectedIndex >= passTypes.length) {
      console.log('❌ Selección inválida');
      alert('❌ Selección inválida. Por favor, escribe un número válido.');
      return;
    }

    const selectedPass = passTypes[selectedIndex];
    console.log('✅ Bono seleccionado:', selectedPass);

    // Confirmar la asignación
    const confirmMessage = `¿Estás seguro de que quieres asignar:\n\n"${selectedPass.passName}"\n- ${selectedPass.classCredits} clases\n- Válido por ${selectedPass.validityInDays} días\n\na ${user.firstName} ${user.lastName}?`;

    console.log('📝 Mostrando confirmación:', confirmMessage);
    if (!confirm(confirmMessage)) {
      console.log('❌ Usuario canceló la confirmación');
      return;
    }

    try {
      console.log('🚀 Asignando bono:', selectedPass.passName, 'a usuario:', user.firstName);

      // Calcular fecha de expiración
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + selectedPass.validityInDays);

      // Actualizar usuario en Firebase
      const updatedUser = {
        membershipType: 'class_pass' as MembershipType,
        activePass: {
          passName: selectedPass.passName,
          price: selectedPass.price || 0,
          creditsRemaining: selectedPass.classCredits,
          expirationDate: expirationDate.toISOString().split('T')[0],
          purchaseDate: new Date().toISOString().split('T')[0],
          passTypeId: selectedPass.id
        },
        updatedAt: new Date()
      };

      console.log('📝 Datos a actualizar:', updatedUser);
      await updateDoc(doc(db, 'users', user.id), updatedUser);

      // Mostrar confirmación
      alert(`✅ ¡Éxito!\n\nBono "${selectedPass.passName}" asignado exitosamente a ${user.firstName} ${user.lastName}.\n\n- Clases restantes: ${selectedPass.classCredits}\n- Expira: ${expirationDate.toLocaleDateString()}`);

      console.log('✅ Bono asignado exitosamente');

      // Recargar usuarios para mostrar los cambios
      loadUsers();

    } catch (error) {
      console.error('❌ Error asignando bono:', error);
      alert('❌ Error al asignar el bono. Por favor, intenta de nuevo.');
    }
  };

  // Cambiar a cuota mensual
  const handleChangeToMonthly = async (user: User) => {
    if (!confirm(`¿Estás seguro de que quieres cambiar a ${user.firstName} de bono de clases a cuota mensual?`)) {
      return;
    }

    try {
      const updatedUser = {
        membershipType: 'monthly_fee' as MembershipType,
        paymentStatus: 'pending' as const,
        activePass: null,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'users', user.id), updatedUser);
      toast.success(`${user.firstName} cambiado a cuota mensual`);
      loadUsers();
    } catch (error) {
      console.error('Error cambiando membresía:', error);
      toast.error('Error al cambiar la membresía');
    }
  };

  // Asignar membresía a usuario
  const handleAssignMembership = async (user: User, membershipId: string) => {
    try {
      // Verificar si ya tiene membresía activa o pausada
      const existingMembership = userMemberships[user.id];
      const hasActiveMembership = existingMembership?.isActive || existingMembership?.isPaused ||
        user.activeMembership?.isActive || user.activeMembership?.isPaused;

      if (hasActiveMembership) {
        toast.error('Este usuario ya tiene una membresía. Cancela la actual antes de asignar una nueva.');
        return;
      }

      const membership = businessMemberships.find(m => m.id === membershipId);
      if (!membership) {
        toast.error('Membresía no encontrada');
        return;
      }

      const startDate = new Date();
      const renewalDate = new Date();
      renewalDate.setMonth(renewalDate.getMonth() + 1);
      renewalDate.setDate(1); // Renovación el día 1 del próximo mes

      // Verificar si existe documento de membresía para actualizar o crear
      const userMembershipDoc = userMemberships[user.id];

      if (userMembershipDoc) {
        // Actualizar membresía existente
        await updateDoc(doc(db, 'userMemberships', userMembershipDoc.id), {
          membershipId,
          membershipName: membership.name,
          price: membership.price || 0,
          startDate,
          renewalDate,
          reservationsUsed: 0,
          reservationsRemaining: membership.reservationsPerMonth,
          isActive: true,
          isPaused: false,
          weeklyLimitEnabled: membership.weeklyLimitEnabled || false,
          weeklyClassCount: membership.weeklyClassCount || 0,
          updatedAt: serverTimestamp()
        });
      } else {
        // Crear nueva membresía de usuario
        await addDoc(collection(db, 'userMemberships'), {
          membershipId,
          membershipName: membership.name,
          price: membership.price || 0,
          userId: user.id,
          businessId,
          startDate,
          renewalDate,
          reservationsUsed: 0,
          reservationsRemaining: membership.reservationsPerMonth,
          isActive: true,
          isPaused: false,
          weeklyLimitEnabled: membership.weeklyLimitEnabled || false,
          weeklyClassCount: membership.weeklyClassCount || 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      // Actualizar estado del usuario con información completa de membresía
      // NO eliminar el bono si existe - el usuario puede tener ambos
      const userUpdateData: any = {
        paymentStatus: 'paid',
        membershipStartDate: startDate,
        nextDueDate: renewalDate,
        // Guardar información de membresía activa directamente en el usuario
        activeMembership: {
          membershipId: membership.id,
          membershipName: membership.name,
          startDate: startDate.toISOString(),
          renewalDate: renewalDate.toISOString(),
          reservationsUsed: 0,
          reservationsRemaining: membership.reservationsPerMonth,
          reservationsPerMonth: membership.reservationsPerMonth,
          timeSlotRestriction: membership.timeRestriction || 'all_day',
          classTypeRestrictions: membership.allowedClassTypeIds || [],
          isActive: true,
          isPaused: false
        },
        updatedAt: serverTimestamp()
      };

      // Solo cambiar membershipType si no tiene bono activo
      if (!user.activePass || user.activePass.creditsRemaining <= 0) {
        userUpdateData.membershipType = 'monthly_fee';
      }

      await updateDoc(doc(db, 'users', user.id), userUpdateData);

      toast.success(`Membresía "${membership.name}" asignada a ${user.firstName}`);
      setAssignModal({ open: false, user: null, type: 'membership' });

      // Actualizar selectedUserDetails si es el mismo usuario
      if (selectedUserDetails?.id === user.id) {
        setSelectedUserDetails({
          ...user,
          activeMembership: userUpdateData.activeMembership,
          membershipType: userUpdateData.membershipType || user.membershipType
        });
      }

      await Promise.all([loadUsers(), loadUserMemberships()]);
    } catch (error) {
      console.error('Error asignando membresía:', error);
      toast.error('Error al asignar la membresía');
    }
  };

  // Pausar/Reanudar membresía
  const handleToggleMembershipPause = async (user: User) => {
    const membership = userMemberships[user.id];
    const userActiveMembership = user.activeMembership;

    if (!membership && !userActiveMembership) return;

    try {
      const isPaused = membership ? !membership.isPaused : !userActiveMembership?.isPaused;

      // Actualizar userMemberships si existe
      if (membership) {
        await updateDoc(doc(db, 'userMemberships', membership.id), {
          isPaused,
          isActive: !isPaused,
          updatedAt: serverTimestamp()
        });
      }

      // Actualizar activeMembership en el usuario
      await updateDoc(doc(db, 'users', user.id), {
        'activeMembership.isPaused': isPaused,
        'activeMembership.isActive': !isPaused,
        updatedAt: serverTimestamp()
      });

      toast.success(`Membresía ${isPaused ? 'pausada' : 'reanudada'} para ${user.firstName}`);
      await Promise.all([loadUsers(), loadUserMemberships()]);
    } catch (error) {
      console.error('Error pausando membresía:', error);
      toast.error('Error al cambiar estado de la membresía');
    }
  };

  // Cancelar membresía
  const handleCancelMembership = async (user: User) => {
    const membership = userMemberships[user.id];
    const hasActiveMembership = membership || user.activeMembership ||
      (user.membershipType === 'monthly_fee' && user.paymentStatus === 'paid');

    if (!hasActiveMembership) return;

    if (!confirm(`¿Estás seguro de que quieres cancelar la membresía de ${user.firstName}?`)) return;

    try {
      // Eliminar de userMemberships si existe
      if (membership) {
        await deleteDoc(doc(db, 'userMemberships', membership.id));
      }

      // Limpiar TODOS los datos de membresía del usuario
      await updateDoc(doc(db, 'users', user.id), {
        paymentStatus: null, // Eliminar estado de pago
        activeMembership: null, // Eliminar membresía activa
        membershipType: null, // Eliminar tipo de membresía
        membershipStartDate: null, // Eliminar fecha de inicio
        nextDueDate: null, // Eliminar fecha de vencimiento
        updatedAt: serverTimestamp()
      });

      toast.success(`Membresía cancelada para ${user.firstName}`);
      await Promise.all([loadUsers(), loadUserMemberships()]);
    } catch (error) {
      console.error('Error cancelando membresía:', error);
      toast.error('Error al cancelar la membresía');
    }
  };

  // Renovación rápida de membresía (1 clic)
  const handleQuickRenewMembership = async (user: User) => {
    const membership = userMemberships[user.id];

    if (!membership) {
      toast.error('No se encontró la membresía para renovar');
      return;
    }

    try {
      // Calcular nueva fecha de renovación (+1 mes desde hoy)
      const newRenewalDate = new Date();
      newRenewalDate.setMonth(newRenewalDate.getMonth() + 1);
      newRenewalDate.setDate(1); // Día 1 del próximo mes

      // Obtener datos de la membresía base para resetear reservaciones
      const membershipBase = businessMemberships.find(m => m.id === membership.membershipId);
      const reservationsPerMonth = membershipBase?.reservationsPerMonth || membership.reservationsPerMonth;

      // Actualizar userMemberships
      await updateDoc(doc(db, 'userMemberships', membership.id), {
        renewalDate: newRenewalDate,
        reservationsUsed: 0,
        reservationsRemaining: reservationsPerMonth,
        isActive: true,
        isPaused: false,
        updatedAt: serverTimestamp()
      });

      // Actualizar activeMembership en el usuario
      await updateDoc(doc(db, 'users', user.id), {
        'activeMembership.renewalDate': newRenewalDate.toISOString(),
        'activeMembership.reservationsUsed': 0,
        'activeMembership.isActive': true,
        'activeMembership.isPaused': false,
        nextDueDate: newRenewalDate,
        updatedAt: serverTimestamp()
      });

      toast.success(`Membresía renovada para ${user.firstName} hasta ${newRenewalDate.toLocaleDateString()}`);

      // Actualizar estado local inmediatamente
      setUserMemberships(prev => ({
        ...prev,
        [user.id]: {
          ...prev[user.id],
          renewalDate: newRenewalDate,
          reservationsUsed: 0,
          reservationsRemaining: reservationsPerMonth,
          isActive: true,
          isPaused: false
        }
      }));

      if (selectedUserDetails?.id === user.id) {
        setSelectedUserDetails(prev => prev ? {
          ...prev,
          activeMembership: prev.activeMembership ? {
            ...prev.activeMembership,
            renewalDate: newRenewalDate.toISOString(),
            reservationsUsed: 0,
            isActive: true,
            isPaused: false
          } : undefined
        } : null);
      }
    } catch (error) {
      console.error('Error renovando membresía:', error);
      toast.error('Error al renovar la membresía');
    }
  };

  // Toggle auto-renovación
  const handleToggleAutoRenewal = async (user: User) => {
    const membership = userMemberships[user.id];

    if (!membership) {
      toast.error('No se encontró la membresía');
      return;
    }

    try {
      const newAutoRenewal = !membership.autoRenewal;

      // Actualizar userMemberships
      await updateDoc(doc(db, 'userMemberships', membership.id), {
        autoRenewal: newAutoRenewal,
        updatedAt: serverTimestamp()
      });

      // Actualizar activeMembership en el usuario
      await updateDoc(doc(db, 'users', user.id), {
        'activeMembership.autoRenewal': newAutoRenewal,
        updatedAt: serverTimestamp()
      });

      toast.success(`Auto-renovación ${newAutoRenewal ? 'activada' : 'desactivada'} para ${user.firstName}`);

      // Actualizar estado local inmediatamente
      setUserMemberships(prev => ({
        ...prev,
        [user.id]: {
          ...prev[user.id],
          autoRenewal: newAutoRenewal
        }
      }));

      if (selectedUserDetails?.id === user.id) {
        setSelectedUserDetails(prev => prev ? {
          ...prev,
          activeMembership: prev.activeMembership ? {
            ...prev.activeMembership,
            autoRenewal: newAutoRenewal
          } : undefined
        } : null);
      }
    } catch (error) {
      console.error('Error configurando auto-renovación:', error);
      toast.error('Error al configurar auto-renovación');
    }
  };

  // Verificar si la membresía está expirada
  const isMembershipExpired = (user: User): boolean => {
    const membership = userMemberships[user.id];
    if (!membership) return false;
    return membership.renewalDate < new Date();
  };

  // Asignar bono a usuario (versión mejorada)
  // NO elimina la membresía existente - el usuario puede tener ambos
  const handleAssignPass = async (user: User, passTypeId: string) => {
    try {
      // Verificar si ya tiene un bono activo
      const hasActivePass = user.activePass &&
        (user.activePass.creditsRemaining > 0 || !user.activePass.expirationDate ||
          new Date(user.activePass.expirationDate) > new Date());

      if (hasActivePass) {
        toast.error('Este usuario ya tiene un bono activo. Cancela el actual antes de asignar uno nuevo.');
        return;
      }

      const passType = passTypes.find(p => p.id === passTypeId);
      if (!passType) {
        toast.error('Bono no encontrado');
        return;
      }

      const purchaseDate = new Date();
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + passType.validityInDays);

      // NO eliminar membresía existente - el usuario puede tener ambos
      // Solo asignamos el nuevo bono

      // Preparar datos de actualización - NO eliminar membresía
      const userUpdateData: any = {
        // Asignar el bono
        activePass: {
          passName: passType.passName,
          passTypeName: passType.passName,
          passTypeId: passType.id,
          price: passType.price || 0,
          creditsRemaining: passType.classCredits,
          totalCredits: passType.classCredits,
          expirationDate: expirationDate.toISOString(),
          purchaseDate: purchaseDate.toISOString(),
          isBlocked: false,
          // Copiar restricciones del tipo de bono
          timeSlotRestriction: passType.timeRestriction || 'all_day',
          classRestriction: passType.classRestriction || 'all',
          classTypeRestrictions: passType.allowedClassTypeIds || []
        },
        updatedAt: serverTimestamp()
      };

      // Solo cambiar membershipType si no tiene membresía activa
      const hasMembership = userMemberships[user.id]?.isActive || user.activeMembership?.isActive;
      if (!hasMembership) {
        userUpdateData.membershipType = 'class_pass';
      }

      await updateDoc(doc(db, 'users', user.id), userUpdateData);

      console.log('🎟️ Bono asignado con restricciones:', {
        timeRestriction: passType.timeRestriction,
        classRestriction: passType.classRestriction,
        allowedClassTypeIds: passType.allowedClassTypeIds
      });
      toast.success(`Bono "${passType.passName}" asignado a ${user.firstName}`);
      setAssignModal({ open: false, user: null, type: 'pass' });

      // Actualizar selectedUserDetails si es el mismo usuario
      if (selectedUserDetails?.id === user.id) {
        setSelectedUserDetails({
          ...user,
          activePass: userUpdateData.activePass,
          membershipType: userUpdateData.membershipType || user.membershipType
        });
      }

      await Promise.all([loadUsers(), loadUserMemberships()]);
    } catch (error) {
      console.error('Error asignando bono:', error);
      toast.error('Error al asignar el bono');
    }
  };

  // Cancelar bono (eliminar completamente)
  const handleCancelPass = async (user: User) => {
    if (!user.activePass) return;

    if (!confirm(`¿Estás seguro de que quieres cancelar el bono "${user.activePass.passName || user.activePass.passTypeName}" de ${user.firstName}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.id), {
        activePass: null,
        membershipType: null,
        updatedAt: serverTimestamp()
      });

      toast.success(`Bono cancelado para ${user.firstName}`);

      // Actualizar estado local inmediatamente
      setUsers(prev => prev.map(u =>
        u.id === user.id
          ? { ...u, activePass: null as any, membershipType: null as any }
          : u
      ));

      // Si tenemos detalles abiertos de este usuario, actualizar
      if (selectedUserDetails?.id === user.id) {
        setSelectedUserDetails(prev => prev ? { ...prev, activePass: null as any, membershipType: null as any } : null);
      }
    } catch (error) {
      console.error('Error cancelando bono:', error);
      toast.error('Error al cancelar el bono');
    }
  };

  // Verificar límite de usuarios
  const checkUserLimit = (): boolean => {
    if (!currentBusiness?.subscription?.plan) return true;

    const plan = currentBusiness.subscription.plan as 'free' | 'basic' | 'pro';
    const maxUsers = systemConfig.plans[plan]?.maxUsers ?? 50;

    if (maxUsers === -1) return true; // Ilimitado

    // Contar usuarios activos actualmente
    const activeUsersCount = users.filter(u => u.status === 'active').length;

    if (activeUsersCount >= maxUsers) {
      toast.error(`Tu plan actual (${systemConfig.plans[plan]?.name}) solo permite ${maxUsers} usuarios activos. Actualiza tu plan para aceptar más usuarios.`);
      return false;
    }

    return true;
  };

  // Aprobar usuario pendiente
  const handleApproveUser = async (userId: string) => {
    // Verificar límite antes de aprobar
    if (!checkUserLimit()) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        status: 'active',
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast.success('Usuario aprobado correctamente');

      // Actualizar estado local
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, status: 'active' as any } : u
      ));

      // Si tenemos detalles abiertos de este usuario, actualizar
      if (selectedUserDetails?.id === userId) {
        setSelectedUserDetails(prev => prev ? { ...prev, status: 'active' as any } : null);
      }
    } catch (error) {
      console.error('Error aprobando usuario:', error);
      toast.error('Error al aprobar usuario');
    }
  };

  // Rechazar usuario pendiente
  const handleRejectUser = async (userId: string) => {
    if (!confirm('¿Estás seguro de rechazar a este usuario? Se marcará como rechazado.')) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast.success('Usuario rechazado');

      // Actualizar estado local
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, status: 'rejected' as any } : u
      ));

      // Si tenemos detalles abiertos de este usuario, cerrar panel
      if (selectedUserDetails?.id === userId) {
        setSelectedUserDetails(null);
      }
    } catch (error) {
      console.error('Error rechazando usuario:', error);
      toast.error('Error al rechazar usuario');
    }
  };

  // Eliminar usuario permanentemente (Auth + Firestore)
  const handleDeleteUser = async (user: User) => {
    // Doble confirmación por seguridad
    if (!confirm(`⚠️ ¿Estás seguro de ELIMINAR PERMANENTEMENTE a ${user.firstName} ${user.lastName}?\n\nEsta acción NO se puede deshacer.`)) return;

    if (!confirm(`🚨 ÚLTIMA CONFIRMACIÓN:\n\nSe eliminará completamente la cuenta de:\n- ${user.firstName} ${user.lastName}\n- ${user.email}\n\n¿Continuar?`)) return;

    const toastId = toast.loading('Eliminando usuario...');

    try {
      console.log('🗑️ Iniciando proceso de eliminación para:', user.id);

      // Usar Cloud Function para eliminar de Auth y Firestore atómicamente
      const deleteUserFn = httpsCallable(functions, 'deleteUser');

      const result = await deleteUserFn({ userId: user.id });
      console.log('✅ Resultado de eliminación:', result.data);

      toast.success(`Usuario ${user.firstName} ${user.lastName} eliminado correctamente`, { id: toastId });

      // Actualizar estado local
      setUsers(prev => prev.filter(u => u.id !== user.id));

      // Cerrar panel de detalles si estaba abierto
      if (selectedUserDetails?.id === user.id) {
        setSelectedUserDetails(null);
      }
    } catch (error: any) {
      console.error('❌ Error eliminando usuario:', error);
      toast.error(`Error al eliminar usuario: ${error.message || 'Error desconocido'}`, { id: toastId });
    }
  };

  // Cambiar estado del usuario
  const handleChangeStatus = async (user: User, newStatus: UserStatus) => {
    // Si se intenta activar, verificar límite
    if (newStatus === 'active' && user.status !== 'active') {
      if (!checkUserLimit()) return;
    }

    const statusText = newStatus === 'active' ? 'activar' : 'suspender';
    if (!confirm(`¿Estás seguro de que quieres ${statusText} a ${user.firstName}?`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.id), {
        status: newStatus,
        updatedAt: new Date()
      });
      toast.success(`Estado de ${user.firstName} cambiado a ${newStatus}`);
      loadUsers();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  // Obtener icono de membresía
  const getMembershipIcon = (membershipType: MembershipType) => {
    return membershipType === 'monthly_fee' ? <CreditCard className="w-4 h-4" /> : <Package className="w-4 h-4" />;
  };

  // Obtener color de estado
  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'pending_approval':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Función para abrir modal de restablecimiento de contraseña
  const openResetPasswordModal = (user: User) => {
    setSelectedUserForReset(user);
    setNewPassword('');
    setShowPassword(false);
    setResetPasswordModal(true);
  };

  // Función para restablecer contraseña
  const handleResetPassword = async () => {
    if (!selectedUserForReset || !newPassword.trim()) {
      toast.error('Por favor ingresa una nueva contraseña');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setResetLoading(true);
    try {
      // Enviar email de restablecimiento de contraseña
      await sendPasswordResetEmail(getAuth(), selectedUserForReset.email);

      toast.success(`Se ha enviado un email de restablecimiento de contraseña a ${selectedUserForReset.email}`);
      setResetPasswordModal(false);
      setSelectedUserForReset(null);
      setNewPassword('');
    } catch (error: any) {
      console.error('Error al restablecer contraseña:', error);
      let message = 'Error al restablecer la contraseña';

      if (error.code === 'auth/user-not-found') {
        message = 'Usuario no encontrado';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Email inválido';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Demasiados intentos. Intenta más tarde';
      }

      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  // Función para cerrar modal de restablecimiento
  const closeResetPasswordModal = () => {
    setResetPasswordModal(false);
    setSelectedUserForReset(null);
    setNewPassword('');
    setShowPassword(false);
    setResetLoading(false);
  };

  // Funciones para editar fecha de inicio de membresía
  const openEditMembershipDateModal = (user: User) => {
    setSelectedUserForDateEdit(user);
    setNewMembershipDate(user.membershipStartDate ?
      user.membershipStartDate.toISOString().split('T')[0] :
      new Date().toISOString().split('T')[0]
    );
    setEditMembershipDateModal(true);
  };

  const handleUpdateMembershipDate = async () => {
    if (!selectedUserForDateEdit || !newMembershipDate) return;

    setDateEditLoading(true);
    try {
      const newDate = new Date(newMembershipDate);

      await updateDoc(doc(db, 'users', selectedUserForDateEdit.id), {
        membershipStartDate: newDate,
        updatedAt: new Date()
      });

      // Actualizar estado local
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === selectedUserForDateEdit.id
            ? { ...u, membershipStartDate: newDate }
            : u
        )
      );

      toast.success(`Fecha de inicio de membresía actualizada para ${selectedUserForDateEdit.firstName} ${selectedUserForDateEdit.lastName}`);
      closeEditMembershipDateModal();
    } catch (error) {
      console.error('Error actualizando fecha de membresía:', error);
      toast.error('Error al actualizar la fecha de membresía');
    } finally {
      setDateEditLoading(false);
    }
  };

  const closeEditMembershipDateModal = () => {
    setEditMembershipDateModal(false);
    setSelectedUserForDateEdit(null);
    setNewMembershipDate('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>No hay un negocio activo seleccionado.</p>
          <p className="text-sm">Accede como administrador de un negocio para gestionar usuarios.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestión de Usuarios</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-amber-400">
              Gestión de Usuarios
            </h1>
            <p className="text-gray-400 mt-2">
              Administra los usuarios del sistema y sus membresías
            </p>
            <div className="text-xs text-gray-500 mt-1">
              Tipos de bonos disponibles: {passTypes.length}
            </div>
          </div>
          <div className="flex gap-2">
            {/* Botones de acción aquí si es necesario */}
          </div>
        </div>

        {/* Invite Link Section */}
        {currentBusiness && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
            <div className="flex flex-col md:flex-row gap-6 items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <QrCode className="w-5 h-5 text-amber-400" />
                  <h3 className="text-lg font-semibold text-white">
                    Enlace de Invitación
                  </h3>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Comparte este enlace o código QR con nuevos usuarios para que puedan registrarse en tu negocio
                </p>
                <div className="flex gap-2">
                  <Input
                    value={getCleanInviteLink()}
                    readOnly
                    className="flex-1 bg-gray-800 text-white font-mono text-sm border-gray-600"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(getCleanInviteLink());
                      setCopiedLink(true);
                      toast.success('Enlace copiado al portapapeles');
                      setTimeout(() => setCopiedLink(false), 2000);
                    }}
                    className="flex items-center gap-2"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copiar
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        if (!qrCodeUrl) return;
                        const res = await fetch(qrCodeUrl);
                        const blob = await res.blob();
                        // Intentar copiar imagen al portapapeles
                        // @ts-ignore
                        if (navigator.clipboard && window.ClipboardItem) {
                          // @ts-ignore
                          await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
                          toast.success('Imagen del QR copiada al portapapeles');
                        } else {
                          // Fallback: descargar la imagen
                          const a = document.createElement('a');
                          a.href = qrCodeUrl;
                          a.download = 'invite-qr.png';
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          toast('Descargando imagen del QR (copiado no soportado)', { icon: '⬇️' });
                        }
                      } catch (e) {
                        console.error('Error copiando QR:', e);
                        toast.error('No se pudo copiar el QR. Se descargará la imagen.');
                        const a = document.createElement('a');
                        a.href = qrCodeUrl;
                        a.download = 'invite-qr.png';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }
                    }}
                  >
                    Copiar imagen
                  </Button>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  {qrCodeUrl && (
                    <img
                      src={qrCodeUrl}
                      alt="QR Code de invitación"
                      className="w-[150px] h-[150px]"
                    />
                  )}
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Escanea para registrarte
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Buscar
              </label>
              <Input
                placeholder="Nombre o email..."
                value={filter.search || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Estado
              </label>
              <select
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={filter.status || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value as UserStatus || undefined }))}
              >
                <option value="">Todos los estados</option>
                <option value="active">Activo</option>
                <option value="pending">Pendiente</option>
                <option value="suspended">Suspendido</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Membresía
              </label>
              <select
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={filter.membershipType || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, membershipType: e.target.value as MembershipType || undefined }))}
              >
                <option value="">Todas las membresías</option>
                <option value="monthly_fee">Cuota Mensual</option>
                <option value="class_pass">Bono de Clases</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Usuarios - Simplificada */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-medium text-amber-400 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-medium text-amber-400 uppercase tracking-wider w-16">
                    Chat
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-amber-400 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-amber-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-medium text-amber-400 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-medium text-amber-400 uppercase tracking-wider">
                    Suscripción
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 divide-y divide-gray-800">
                {filteredUsers.map((user) => {
                  const subscription = getSubscriptionStatus(user);
                  return (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedUserDetails(user)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <ProfilePhoto
                            photoUrl={user.profilePhotoUrl}
                            name={`${user.firstName} ${user.lastName}`}
                            size="sm"
                            className="mr-3"
                          />
                          <div className="text-sm font-medium text-white">
                            {user.firstName} {user.lastName}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            setSelectedUserForChat(user);
                            setChatModalOpen(true);
                          }}
                          className="relative p-2 text-gray-400 hover:text-amber-400 hover:bg-gray-700 rounded-lg transition-colors mx-auto"
                          title="Chat con usuario"
                        >
                          <MessageCircle className="w-5 h-5" />
                          {usersWithUnreadMessages.has(user.id) && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {user.phone || '−'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                        {user.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {user.status === 'pending_approval' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/50 animate-pulse">
                            <UserPlus className="w-3 h-3" />
                            Aprobar
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                            {user.status === 'active' ? 'Activo' :
                              user.status === 'pending' ? 'Pendiente' :
                                user.status === 'rejected' ? 'Rechazado' : 'Suspendido'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${subscription.color}`}>
                          {subscription.icon} {subscription.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-white">
                {filter.search || filter.status || filter.membershipType
                  ? 'Intenta ajustar los filtros de búsqueda'
                  : 'No hay usuarios registrados en el sistema'
                }
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {filter.search || filter.status || filter.membershipType
                  ? 'No se encontraron usuarios con los filtros aplicados'
                  : 'Los usuarios aparecerán aquí cuando se registren'
                }
              </p>
            </div>
          )}
        </div>

        {/* Panel lateral de detalles del usuario */}
        {selectedUserDetails && (
          <>
            <div
              className="fixed inset-0 bg-black/60 z-40"
              onClick={() => setSelectedUserDetails(null)}
            />
            <div className="fixed right-0 top-0 h-full w-full max-w-md bg-gray-900 border-l border-gray-800 z-50 overflow-y-auto shadow-2xl">
              {/* Header del panel */}
              <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Detalles del Usuario</h3>
                <button
                  onClick={() => setSelectedUserDetails(null)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Contenido del panel */}
              <div className="p-4 space-y-6">
                {/* Perfil */}
                <div className="flex items-center gap-4">
                  <ProfilePhoto
                    photoUrl={selectedUserDetails.profilePhotoUrl}
                    name={`${selectedUserDetails.firstName} ${selectedUserDetails.lastName}`}
                    size="lg"
                  />
                  <div>
                    <h4 className="text-xl font-bold text-white">
                      {selectedUserDetails.firstName} {selectedUserDetails.lastName}
                    </h4>
                    <p className="text-gray-400">{selectedUserDetails.email}</p>
                    {selectedUserDetails.phone && (
                      <p className="text-gray-500">{selectedUserDetails.phone}</p>
                    )}
                  </div>
                </div>

                {/* Alerta de Aprobación Pendiente */}
                {selectedUserDetails.status === 'pending_approval' && (
                  <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
                        <UserPlus className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h5 className="text-amber-400 font-semibold">Pendiente de Aprobación</h5>
                        <p className="text-amber-300/70 text-sm">Este usuario necesita tu aprobación para acceder</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproveUser(selectedUserDetails.id)}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Aprobar Usuario
                      </button>
                      <button
                        onClick={() => handleRejectUser(selectedUserDetails.id)}
                        className="flex-1 px-4 py-2 bg-red-600/20 text-red-400 border border-red-600/50 rounded-lg hover:bg-red-600/40 transition-colors flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Rechazar
                      </button>
                    </div>
                  </div>
                )}

                {/* Estado y Suscripción */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Estado</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedUserDetails.status)}`}>
                      {selectedUserDetails.status === 'active' ? 'Activo' :
                        selectedUserDetails.status === 'pending' ? 'Pendiente' :
                          selectedUserDetails.status === 'pending_approval' ? 'Pendiente Aprobación' :
                            selectedUserDetails.status === 'rejected' ? 'Rechazado' : 'Suspendido'}
                    </span>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Suscripción</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSubscriptionStatus(selectedUserDetails).color}`}>
                      {getSubscriptionStatus(selectedUserDetails).icon} {getSubscriptionStatus(selectedUserDetails).label}
                    </span>
                  </div>
                </div>

                {/* Información de Membresía - Mostrar si tiene membresía (independiente de si tiene bono) */}
                {(userMemberships[selectedUserDetails.id] || selectedUserDetails.activeMembership) && (
                  <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 rounded-xl p-4 border border-amber-700/50">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Membresía {(userMemberships[selectedUserDetails.id]?.isActive || selectedUserDetails.activeMembership?.isActive) ? 'Activa' : 'Pendiente'}
                      </h5>
                      {(userMemberships[selectedUserDetails.id]?.isPaused || selectedUserDetails.activeMembership?.isPaused) && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Pausada</span>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(userMemberships[selectedUserDetails.id] || selectedUserDetails.activeMembership) ? (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Plan</span>
                            <span className="text-white font-medium">
                              {userMemberships[selectedUserDetails.id]?.membershipName || selectedUserDetails.activeMembership?.membershipName || 'Membresía'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Desde</span>
                            <span className="text-white">
                              {formatDate(userMemberships[selectedUserDetails.id]?.startDate || (selectedUserDetails.activeMembership?.startDate ? new Date(selectedUserDetails.activeMembership.startDate) : new Date()))}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Renueva</span>
                            <span className="text-white">
                              {formatDate(userMemberships[selectedUserDetails.id]?.renewalDate || (selectedUserDetails.activeMembership?.renewalDate ? new Date(selectedUserDetails.activeMembership.renewalDate) : new Date()))}
                            </span>
                          </div>
                          <div className="bg-gray-900/50 rounded-lg p-3">
                            {(() => {
                              // Calcular clases usadas desde: total - restantes
                              const membershipData = userMemberships[selectedUserDetails.id] || selectedUserDetails.activeMembership;
                              const totalPerMonth = membershipData?.reservationsPerMonth || 0;
                              const remaining = userMemberships[selectedUserDetails.id]?.reservationsRemaining ??
                                (selectedUserDetails.activeMembership as any)?.reservationsRemaining ?? totalPerMonth;
                              const used = Math.max(0, totalPerMonth - remaining);

                              return (
                                <>
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-400 text-sm">Clases este mes</span>
                                    <span className="text-white font-bold">
                                      {used} / {totalPerMonth}
                                    </span>
                                  </div>
                                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                                      style={{ width: `${totalPerMonth > 0 ? (used / totalPerMonth) * 100 : 0}%` }}
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {remaining} reservas restantes
                                  </p>
                                </>
                              );
                            })()}
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Horario</span>
                            <span className={`px-2 py-0.5 rounded-full ${(userMemberships[selectedUserDetails.id]?.timeRestriction || selectedUserDetails.activeMembership?.timeSlotRestriction) === 'all_day' ? 'bg-green-500/20 text-green-400' :
                              (userMemberships[selectedUserDetails.id]?.timeRestriction || selectedUserDetails.activeMembership?.timeSlotRestriction) === 'morning' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-purple-500/20 text-purple-400'
                              }`}>
                              {(userMemberships[selectedUserDetails.id]?.timeRestriction || selectedUserDetails.activeMembership?.timeSlotRestriction) === 'all_day' ? 'Todo el día' :
                                (userMemberships[selectedUserDetails.id]?.timeRestriction || selectedUserDetails.activeMembership?.timeSlotRestriction) === 'morning' ? 'Mañanas' : 'Tardes'}
                            </span>
                          </div>

                          {/* Alerta de membresía expirada + botón de renovación rápida */}
                          {isMembershipExpired(selectedUserDetails) && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-2">
                              <p className="text-red-400 text-xs mb-2">⚠️ Membresía caducada</p>
                              <button
                                onClick={() => handleQuickRenewMembership(selectedUserDetails)}
                                className="w-full py-2 px-3 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                              >
                                🔄 Renovar Membresía
                              </button>
                            </div>
                          )}

                          {/* Checkbox de auto-renovación */}
                          <div className="flex items-center justify-between mt-3 p-2 bg-gray-900/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`autoRenewal-${selectedUserDetails.id}`}
                                checked={userMemberships[selectedUserDetails.id]?.autoRenewal || selectedUserDetails.activeMembership?.autoRenewal || false}
                                onChange={() => handleToggleAutoRenewal(selectedUserDetails)}
                                className="w-4 h-4 text-amber-500 bg-gray-700 border-gray-600 rounded focus:ring-amber-500 focus:ring-2"
                              />
                              <label htmlFor={`autoRenewal-${selectedUserDetails.id}`} className="text-xs text-gray-300">
                                Auto-renovación
                              </label>
                            </div>
                            <span className="text-xs text-gray-500">
                              {userMemberships[selectedUserDetails.id]?.autoRenewal || selectedUserDetails.activeMembership?.autoRenewal ? '✓ Activa' : 'Inactiva'}
                            </span>
                          </div>

                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => handleToggleMembershipPause(selectedUserDetails)}
                              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${(userMemberships[selectedUserDetails.id]?.isPaused || selectedUserDetails.activeMembership?.isPaused)
                                ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                                : 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30'
                                }`}
                            >
                              {(userMemberships[selectedUserDetails.id]?.isPaused || selectedUserDetails.activeMembership?.isPaused) ? '▶ Reanudar' : '⏸ Pausar'}
                            </button>
                            <button
                              onClick={() => handleCancelMembership(selectedUserDetails)}
                              className="flex-1 py-2 px-3 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
                            >
                              ✕ Cancelar
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Tipo</span>
                            <span className="text-white font-medium">Cuota Mensual</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Estado</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${selectedUserDetails.paymentStatus === 'paid'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                              {selectedUserDetails.paymentStatus === 'paid' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </div>
                          {selectedUserDetails.membershipStartDate && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-400 text-sm">Desde</span>
                              <span className="text-white">{formatDate(selectedUserDetails.membershipStartDate)}</span>
                            </div>
                          )}
                          {selectedUserDetails.nextDueDate && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-400 text-sm">Vence</span>
                              <span className={`font-medium ${calculateDaysRemaining(selectedUserDetails.nextDueDate) <= 3 ? 'text-red-400' :
                                calculateDaysRemaining(selectedUserDetails.nextDueDate) <= 7 ? 'text-yellow-400' : 'text-white'
                                }`}>
                                {formatDate(selectedUserDetails.nextDueDate)} ({calculateDaysRemaining(selectedUserDetails.nextDueDate)} días)
                              </span>
                            </div>
                          )}
                          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3 mt-2">
                            <p className="text-xs text-blue-400">
                              💡 Para asignar un plan con restricciones y seguimiento de clases, usa el botón "Asignar Membresía"
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Información de Bono */}
                {selectedUserDetails.activePass && (
                  <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/20 rounded-xl p-4 border border-blue-700/50">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-blue-400 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Bono Activo
                      </h5>
                      {(selectedUserDetails.activePass as any).isBlocked && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">Bloqueado</span>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Bono</span>
                        <span className="text-white font-medium">{selectedUserDetails.activePass.passTypeName || selectedUserDetails.activePass.passName || 'Bono'}</span>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-400 text-sm">Clases</span>
                          <span className="text-white font-bold">
                            {selectedUserDetails.activePass.creditsRemaining} / {(selectedUserDetails.activePass as any).creditsTotal || selectedUserDetails.activePass.creditsRemaining}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${selectedUserDetails.activePass.creditsRemaining <= 2 ? 'bg-red-500' :
                              selectedUserDetails.activePass.creditsRemaining <= 5 ? 'bg-yellow-500' : 'bg-blue-500'
                              }`}
                            style={{ width: `${(selectedUserDetails.activePass.creditsRemaining / ((selectedUserDetails.activePass as any).creditsTotal || 10)) * 100}%` }}
                          />
                        </div>
                      </div>
                      {selectedUserDetails.activePass.expirationDate && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Caduca</span>
                          <span className={`font-medium ${new Date(selectedUserDetails.activePass.expirationDate) < new Date() ? 'text-red-400' :
                            calculateDaysRemaining(new Date(selectedUserDetails.activePass.expirationDate)) <= 7 ? 'text-yellow-400' : 'text-white'
                            }`}>
                            {formatDate(new Date(selectedUserDetails.activePass.expirationDate))}
                          </span>
                        </div>
                      )}
                      {(selectedUserDetails.activePass as any).timeRestriction && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-500">Horario</span>
                          <span className={`px-2 py-0.5 rounded-full ${(selectedUserDetails.activePass as any).timeRestriction === 'all_day' ? 'bg-green-500/20 text-green-400' :
                            (selectedUserDetails.activePass as any).timeRestriction === 'morning' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                            {(selectedUserDetails.activePass as any).timeRestriction === 'all_day' ? 'Todo el día' :
                              (selectedUserDetails.activePass as any).timeRestriction === 'morning' ? 'Mañanas' : 'Tardes'}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => handleCancelPass(selectedUserDetails)}
                        className="w-full py-2 px-3 rounded-lg text-xs font-medium transition-colors bg-red-600/20 text-red-400 hover:bg-red-600/30"
                      >
                        ✕ Cancelar Bono
                      </button>
                    </div>
                  </div>
                )}

                {/* Sin suscripción - Solo mostrar si no tiene NADA */}
                {!selectedUserDetails.membershipType &&
                  !userMemberships[selectedUserDetails.id] &&
                  !selectedUserDetails.activeMembership &&
                  !selectedUserDetails.activePass && (
                    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 text-center">
                      <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Users className="w-6 h-6 text-gray-500" />
                      </div>
                      <p className="text-gray-400 mb-3">Este usuario no tiene membresía ni bono activo</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setAssignModal({ open: true, user: selectedUserDetails, type: 'membership' })}
                          className="flex-1 py-2 px-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          + Membresía
                        </button>
                        <button
                          onClick={() => setAssignModal({ open: true, user: selectedUserDetails, type: 'pass' })}
                          className="flex-1 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          + Bono
                        </button>
                      </div>
                    </div>
                  )}

                {/* Historial de Membresías y Bonos */}
                {(selectedUserDetails.membershipHistory && selectedUserDetails.membershipHistory.length > 0) && (
                  <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-300">Historial de Membresías</span>
                        <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">
                          {selectedUserDetails.membershipHistory.length}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                    </button>

                    {showHistory && (
                      <div className="border-t border-gray-700 max-h-60 overflow-y-auto">
                        {selectedUserDetails.membershipHistory.map((record, index) => (
                          <div
                            key={record.id || index}
                            className="p-3 border-b border-gray-700/50 last:border-0 hover:bg-gray-700/30"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm text-white">
                                  {record.notes || 'Membresía'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {record.paymentDate instanceof Date
                                    ? record.paymentDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : typeof record.paymentDate === 'string'
                                      ? new Date(record.paymentDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
                                      : 'Fecha N/A'
                                  }
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-green-400">
                                  {record.amount ? `${record.amount}€` : '-'}
                                </p>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${record.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                  {record.status === 'paid' ? 'Pagado' : 'Pendiente'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Fecha de Registro */}
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Fecha de Registro</p>
                  <p className="text-white">
                    {selectedUserDetails.createdAt && selectedUserDetails.createdAt instanceof Date && !isNaN(selectedUserDetails.createdAt.getTime())
                      ? selectedUserDetails.createdAt.toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                      : 'N/A'
                    }
                  </p>
                </div>

                {/* Acciones */}
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-amber-400">Acciones</h5>

                  <button
                    onClick={() => {
                      setSelectedUserForChat(selectedUserDetails);
                      setChatModalOpen(true);
                    }}
                    className="w-full flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors text-left"
                  >
                    <MessageCircle className="w-5 h-5 text-amber-400" />
                    <span className="text-white">Enviar Mensaje</span>
                    {usersWithUnreadMessages.has(selectedUserDetails.id) && (
                      <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                  </button>

                  <button
                    onClick={() => startEditUser(selectedUserDetails)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors text-left"
                  >
                    <Edit className="w-5 h-5 text-blue-400" />
                    <span className="text-white">Editar Usuario</span>
                  </button>

                  {/* Botones de asignar membresía/bono */}
                  <button
                    onClick={() => setAssignModal({ open: true, user: selectedUserDetails, type: 'membership' })}
                    className="w-full flex items-center gap-3 p-3 bg-amber-900/30 hover:bg-amber-900/50 rounded-xl transition-colors text-left border border-amber-700/50"
                  >
                    <CreditCard className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-300">Asignar Membresía</span>
                  </button>

                  <button
                    onClick={() => setAssignModal({ open: true, user: selectedUserDetails, type: 'pass' })}
                    className="w-full flex items-center gap-3 p-3 bg-blue-900/30 hover:bg-blue-900/50 rounded-xl transition-colors text-left border border-blue-700/50"
                  >
                    <Package className="w-5 h-5 text-blue-400" />
                    <span className="text-blue-300">Asignar Bono</span>
                  </button>

                  <button
                    onClick={() => openResetPasswordModal(selectedUserDetails)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors text-left"
                  >
                    <Key className="w-5 h-5 text-orange-400" />
                    <span className="text-white">Restablecer Contraseña</span>
                  </button>

                  <button
                    onClick={() => {
                      if (selectedUserDetails.status === 'active') {
                        handleChangeStatus(selectedUserDetails, 'suspended');
                      } else {
                        handleChangeStatus(selectedUserDetails, 'active');
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${selectedUserDetails.status === 'active'
                      ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400'
                      : 'bg-green-900/30 hover:bg-green-900/50 text-green-400'
                      }`}
                  >
                    {selectedUserDetails.status === 'active' ? (
                      <>
                        <UserX className="w-5 h-5" />
                        <span>Suspender Usuario</span>
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-5 h-5" />
                        <span>Activar Usuario</span>
                      </>
                    )}
                  </button>

                  {/* Botón de eliminar usuario */}
                  <button
                    onClick={() => handleDeleteUser(selectedUserDetails)}
                    className="w-full flex items-center gap-3 p-3 bg-red-950/50 hover:bg-red-900/70 rounded-xl transition-colors text-left border border-red-800/50 mt-2"
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                    <span className="text-red-400">Eliminar Usuario</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal para editar usuario */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-lg border border-gray-800 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                Editar Usuario: {editingUser.firstName} {editingUser.lastName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelEdit}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre
                  </label>
                  <Input
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Apellido
                  </label>
                  <Input
                    value={editForm.lastName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Apellido"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <Input
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Email"
                  type="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Teléfono
                </label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Teléfono"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Estado
                  </label>
                  <select
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value as UserStatus }))}
                  >
                    <option value="active">Activo</option>
                    <option value="pending">Pendiente</option>
                    <option value="suspended">Suspendido</option>
                  </select>
                </div>

                {/* Nota: Membresías y bonos se gestionan desde el panel lateral */}
                <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3">
                  <p className="text-xs text-blue-400">
                    💡 Las membresías y bonos se gestionan desde las acciones del panel lateral (Asignar Membresía / Asignar Bono)
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={saveUserChanges}
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
              <Button
                variant="outline"
                onClick={cancelEdit}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para restablecer contraseña */}
      {resetPasswordModal && selectedUserForReset && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-800 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                Restablecer Contraseña
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeResetPasswordModal}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-800">
                <p className="text-sm text-blue-300">
                  <strong>Usuario:</strong> {selectedUserForReset.firstName} {selectedUserForReset.lastName}
                </p>
                <p className="text-sm text-blue-400">
                  <strong>Email:</strong> {selectedUserForReset.email}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Ingresa la nueva contraseña"
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo 6 caracteres
                </p>
              </div>

              <div className="bg-yellow-900/30 p-3 rounded-lg border border-yellow-800">
                <p className="text-sm text-yellow-300">
                  <strong>⚠️ Importante:</strong> Se enviará un email de restablecimiento de contraseña al usuario.
                  El usuario deberá hacer clic en el enlace del email para establecer su nueva contraseña.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleResetPassword}
                disabled={resetLoading || !newPassword.trim() || newPassword.length < 6}
                className="flex-1"
              >
                {resetLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Enviar Email
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={closeResetPasswordModal}
                disabled={resetLoading}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar fecha de inicio de membresía */}
      {editMembershipDateModal && selectedUserForDateEdit && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-800 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                Editar Fecha de Inicio de Membresía
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeEditMembershipDateModal}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-800">
                <p className="text-sm text-blue-300">
                  <strong>Usuario:</strong> {selectedUserForDateEdit.firstName} {selectedUserForDateEdit.lastName}
                </p>
                <p className="text-sm text-blue-400">
                  <strong>Email:</strong> {selectedUserForDateEdit.email}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fecha de Inicio de Membresía
                </label>
                <Input
                  type="date"
                  value={newMembershipDate}
                  onChange={(e) => setNewMembershipDate(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Esta fecha se usará para calcular los vencimientos mensuales
                </p>
              </div>

              <div className="bg-yellow-900/30 p-3 rounded-lg border border-yellow-800">
                <p className="text-sm text-yellow-300">
                  <strong>⚠️ Importante:</strong> Cambiar esta fecha afectará el cálculo de los próximos vencimientos.
                  Los pagos anteriores no se verán afectados.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleUpdateMembershipDate}
                disabled={dateEditLoading || !newMembershipDate}
                className="flex-1"
              >
                {dateEditLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Actualizando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Actualizar Fecha
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={closeEditMembershipDateModal}
                disabled={dateEditLoading}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Chat */}
      {selectedUserForChat && (
        <AdminChatModal
          user={selectedUserForChat}
          isOpen={chatModalOpen}
          onClose={() => {
            setChatModalOpen(false);
            setSelectedUserForChat(null);
          }}
          onMessagesRead={() => {
            setUsersWithUnreadMessages(prev => {
              const next = new Set(prev);
              next.delete(selectedUserForChat.id);
              return next;
            });
          }}
        />
      )}

      {/* Modal para asignar Membresía o Bono */}
      {assignModal.open && assignModal.user && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-lg border border-gray-800 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gray-800 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Asignar {assignModal.type === 'membership' ? 'Membresía' : 'Bono'}
                </h3>
                <p className="text-sm text-gray-400">
                  a {assignModal.user.firstName} {assignModal.user.lastName}
                </p>
              </div>
              <button
                onClick={() => setAssignModal({ open: false, user: null, type: 'membership' })}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-800">
              <button
                onClick={() => setAssignModal(prev => ({ ...prev, type: 'membership' }))}
                className={`flex-1 py-3 text-center font-medium transition-colors ${assignModal.type === 'membership'
                  ? 'text-amber-400 border-b-2 border-amber-400 bg-gray-800/50'
                  : 'text-gray-500 hover:text-gray-300'
                  }`}
              >
                <CreditCard className="w-4 h-4 inline mr-2" />
                Membresías
              </button>
              <button
                onClick={() => setAssignModal(prev => ({ ...prev, type: 'pass' }))}
                className={`flex-1 py-3 text-center font-medium transition-colors ${assignModal.type === 'pass'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                  : 'text-gray-500 hover:text-gray-300'
                  }`}
              >
                <Package className="w-4 h-4 inline mr-2" />
                Bonos
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {assignModal.type === 'membership' ? (
                businessMemberships.length > 0 ? (
                  <div className="space-y-3">
                    {businessMemberships.map(membership => (
                      <div
                        key={membership.id}
                        className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-amber-500 transition-colors cursor-pointer group"
                        onClick={() => handleAssignMembership(assignModal.user!, membership.id)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white group-hover:text-amber-400 transition-colors">
                            {membership.name}
                          </h4>
                          <span className="text-amber-400 font-bold">{membership.price}€/mes</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-gray-700 rounded-full text-gray-300">
                            {membership.reservationsPerMonth} clases/mes
                          </span>
                          <span className={`px-2 py-1 rounded-full ${membership.timeRestriction === 'all_day' ? 'bg-green-900/50 text-green-400' :
                            membership.timeRestriction === 'morning' ? 'bg-yellow-900/50 text-yellow-400' :
                              'bg-purple-900/50 text-purple-400'
                            }`}>
                            {membership.timeRestriction === 'all_day' ? 'Todo el día' :
                              membership.timeRestriction === 'morning' ? 'Mañanas' : 'Tardes'}
                          </span>
                          <span className={`px-2 py-1 rounded-full ${membership.classRestriction === 'all' ? 'bg-blue-900/50 text-blue-400' : 'bg-orange-900/50 text-orange-400'
                            }`}>
                            {membership.classRestriction === 'all' ? 'Todas las clases' : 'Clases específicas'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No hay membresías disponibles</p>
                    <p className="text-sm text-gray-500 mt-1">Crea membresías en la sección de Membresías y Bonos</p>
                  </div>
                )
              ) : (
                passTypes.length > 0 ? (
                  <div className="space-y-3">
                    {passTypes.filter(p => p.isActive).map(pass => (
                      <div
                        key={pass.id}
                        className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer group"
                        onClick={() => handleAssignPass(assignModal.user!, pass.id)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
                            {pass.passName}
                          </h4>
                          <span className="text-blue-400 font-bold">{pass.price || 0}€</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-gray-700 rounded-full text-gray-300">
                            {pass.classCredits} clases
                          </span>
                          <span className="px-2 py-1 bg-gray-700 rounded-full text-gray-300">
                            Válido {pass.validityInDays} días
                          </span>
                          {(pass as any).timeRestriction && (pass as any).timeRestriction !== 'all_day' && (
                            <span className={`px-2 py-1 rounded-full ${(pass as any).timeRestriction === 'morning' ? 'bg-yellow-900/50 text-yellow-400' :
                              'bg-purple-900/50 text-purple-400'
                              }`}>
                              {(pass as any).timeRestriction === 'morning' ? 'Mañanas' : 'Tardes'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No hay bonos disponibles</p>
                    <p className="text-sm text-gray-500 mt-1">Crea bonos en la sección de Membresías y Bonos</p>
                  </div>
                )
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-800/50 px-6 py-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 text-center">
                Haz clic en una opción para asignarla al usuario
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagementPage; 